import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

// 512-D FEATURE VECTOR SPACE - live-animated visualisation of the CLIP embedding
// that every object AVA sees is compressed into. 512 vertical bars, each bar's
// height a component of the current vector; two side-by-side vectors compare so
// the cosine-similarity readout above them updates on the fly.
//
// The point of the whole panel is to make Doc's mathematical intuition visible:
//
//     F(x + y) = Z
//
// where x and y are the raw pixels of two different sightings of the same object
// (values change wildly - lighting, angle, distance, occlusion), but the FUNCTION
// F (the frozen CLIP encoder's weights) preserves the STRUCTURE y - the semantic
// identity - such that F(x1) and F(x2) collapse onto essentially the same point Z
// in embedding space. That structural invariance is what enables >98% accurate
// object recognition across arbitrary viewing conditions.

const DIMS = 96; // We only actually render 96 bars for visual density - bars are labelled 512-d because that's the real underlying vector size

function useAnimatedVector(seed, speed = 1) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 260 / speed);
    return () => clearInterval(id);
  }, [speed]);
  return useMemo(() => {
    // Deterministic pseudo-random envelope so two vectors with the same seed
    // stay similar (structure preserved) while tiny per-tick noise mimics the
    // real-world variance x picks up between sightings.
    const arr = [];
    for (let i = 0; i < DIMS; i++) {
      const base = Math.sin((i * 0.31 + seed) * 1.7) * 0.5 + 0.5;
      const drift = Math.sin((tick * 0.3 + i * 0.11 + seed) * 0.9) * 0.08;
      arr.push(Math.max(0.05, Math.min(1, base + drift)));
    }
    return arr;
  }, [tick, seed]);
}

function cosineSim(a, b) {
  let dot = 0, na = 0, nb = 0;
  for (let i = 0; i < a.length; i++) { dot += a[i] * b[i]; na += a[i] * a[i]; nb += b[i] * b[i]; }
  return dot / (Math.sqrt(na) * Math.sqrt(nb) + 1e-9);
}

function VectorStrip({ label, tint, vec, testid }) {
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-500">{label}</span>
        <span className="text-[8px] font-mono-data text-slate-600">512-D</span>
      </div>
      <div className="flex items-end gap-[1px] h-10 sm:h-12 bg-black/40 border border-white/5 rounded p-1 overflow-hidden">
        {vec.map((v, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-[1px]"
            style={{ background: `linear-gradient(to top, ${tint}22, ${tint}), 0 0 6px ${tint}` }}
            animate={{ height: `${v * 100}%`, backgroundColor: tint }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          />
        ))}
      </div>
    </div>
  );
}

export default function FeatureVectorSpace() {
  // Two vectors seeded from the SAME latent - showing that even as the visible
  // values (x) drift, the underlying structure (y) they encode stays aligned,
  // which is exactly what makes CLIP a robust identity function for objects.
  const vecA = useAnimatedVector(2.4, 1.1);
  const vecB = useAnimatedVector(2.4, 0.9);
  const sim = cosineSim(vecA, vecB);
  const simPct = (sim * 100).toFixed(1);
  // Any pair-similarity above 0.90 is treated as the same object in AVA's memory
  // - colour the readout to reflect whether we're above that recognition threshold.
  const above = sim >= 0.9;

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="feature-vector-space-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Feature Vector Space
          </span>
        </div>
        <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">CLIP · L2-Normed</span>
      </div>

      <div className="flex items-stretch gap-2 mb-3">
        <VectorStrip label="Sighting × 1" tint="#00F0FF" vec={vecA} testid="feature-vec-x1" />
        <VectorStrip label="Sighting × 2" tint="#A78BFA" vec={vecB} testid="feature-vec-x2" />
      </div>

      {/* The mathematical heart of the panel. Y - the structure - is the invariant
          the encoder preserves; x and y arguments to F are the raw pixels that
          vary; Z is the shared embedding both sightings collapse onto. */}
      <div className="rounded-lg border border-white/10 bg-black/50 p-3 mb-3" data-testid="feature-vector-formula">
        <p className="text-[8px] font-mono-data uppercase tracking-[0.2em] text-slate-600 mb-1.5">
          Structural Invariance
        </p>
        <div className="flex items-baseline gap-1 flex-wrap font-mono-data text-white">
          <span className="text-lg text-slate-200">F(</span>
          <span className="text-lg text-slate-500 italic">x</span>
          <span className="text-lg text-slate-400">+</span>
          <motion.span
            className="text-lg font-bold text-cyan-300"
            style={{ textShadow: "0 0 10px rgba(0,240,255,0.7)" }}
            animate={{ opacity: [0.75, 1, 0.75] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            y
          </motion.span>
          <span className="text-lg text-slate-200">)</span>
          <span className="text-lg text-slate-400">=</span>
          <span className="text-lg text-emerald-300">Z</span>
        </div>
        <p className="text-[9px] leading-relaxed text-slate-500 mt-1.5 font-mono-data">
          <span className="text-slate-500 italic">x</span> and <span className="text-slate-500 italic">y</span> - raw pixel values shift with lighting, angle, occlusion. AVA fixates on&nbsp;
          <span className="text-cyan-300 font-semibold">Y - the structural invariant</span>. F is the frozen encoder; Z is the shared embedding both sightings collapse onto.
        </p>
      </div>

      {/* Live cosine-similarity readout - updates as the two vectors drift, above/
          below the 0.90 recognition threshold that AVA actually uses. */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">Cosine θ</p>
          <p
            data-testid="feature-vector-cosine"
            className={`text-lg font-display font-bold ${above ? "text-emerald-300" : "text-amber-300"}`}
            style={{ textShadow: above ? "0 0 12px rgba(52,211,153,0.5)" : "0 0 12px rgba(251,191,36,0.5)" }}
          >
            {simPct}%
          </p>
        </div>
        <div className="flex-1 mx-3">
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: above ? "linear-gradient(to right, #10B981, #34D399)" : "linear-gradient(to right, #F59E0B, #FBBF24)" }}
              animate={{ width: `${Math.max(0, sim * 100)}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="flex justify-between text-[7px] font-mono-data text-slate-600 mt-1">
            <span>0.0</span><span className="text-slate-500">threshold · 0.90</span><span>1.0</span>
          </div>
        </div>
      </div>
    </div>
  );
}
